const LEGACY_DB_NAME = 'esan-local-media';
const LEGACY_STORE = 'blobs';
const LEGACY_DB_VERSION = 1;
export const LOCAL_MEDIA_IDB_STORE = 'media-blobs';

function isStoredBlob(value: unknown): value is Blob {
  return (
    value instanceof Blob ||
    (typeof value === 'object' &&
      value !== null &&
      typeof (value as Blob).arrayBuffer === 'function' &&
      typeof (value as Blob).size === 'number')
  );
}

function openLegacyMediaDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    const req = indexedDB.open(LEGACY_DB_NAME, LEGACY_DB_VERSION);
    req.onerror = () => resolve(null);
    req.onsuccess = () => resolve(req.result);
  });
}

async function readLegacyBlobPairs(legacy: IDBDatabase): Promise<Array<[IDBValidKey, Blob]>> {
  if (!legacy.objectStoreNames.contains(LEGACY_STORE)) return [];
  return new Promise((resolve, reject) => {
    const tx = legacy.transaction(LEGACY_STORE, 'readonly');
    tx.onerror = () => reject(tx.error ?? new Error('legacy idb read failed'));
    const store = tx.objectStore(LEGACY_STORE);
    const keysReq = store.getAllKeys();
    const valuesReq = store.getAll();
    let keys: IDBValidKey[] = [];
    let values: unknown[] = [];
    let pending = 2;
    const done = () => {
      pending -= 1;
      if (pending > 0) return;
      const pairs: Array<[IDBValidKey, Blob]> = [];
      for (let i = 0; i < keys.length; i += 1) {
        const value = values[i];
        if (isStoredBlob(value)) pairs.push([keys[i]!, value]);
      }
      resolve(pairs);
    };
    keysReq.onerror = () => reject(keysReq.error ?? new Error('legacy idb keys failed'));
    valuesReq.onerror = () => reject(valuesReq.error ?? new Error('legacy idb values failed'));
    keysReq.onsuccess = () => {
      keys = keysReq.result;
      done();
    };
    valuesReq.onsuccess = () => {
      values = valuesReq.result;
      done();
    };
  });
}

async function writeMediaBlobPairs(targetDb: IDBDatabase, pairs: Array<[IDBValidKey, Blob]>): Promise<string[]> {
  const keys = pairs.map(([key]) => String(key));
  await new Promise<void>((resolve, reject) => {
    const tx = targetDb.transaction(LOCAL_MEDIA_IDB_STORE, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('media migrate put failed'));
    const store = tx.objectStore(LOCAL_MEDIA_IDB_STORE);
    for (const [key, blob] of pairs) store.put(blob, String(key));
  });
  return keys;
}

async function verifyMigratedKeys(targetDb: IDBDatabase, keys: string[]): Promise<boolean> {
  for (const key of keys) {
    const blob = await new Promise<Blob | null>((resolve, reject) => {
      const tx = targetDb.transaction(LOCAL_MEDIA_IDB_STORE, 'readonly');
      tx.onerror = () => reject(tx.error ?? new Error('media migrate verify failed'));
      const req = tx.objectStore(LOCAL_MEDIA_IDB_STORE).get(key);
      req.onsuccess = () => resolve(isStoredBlob(req.result) ? req.result : null);
      req.onerror = () => reject(req.error ?? new Error('media migrate verify get failed'));
    });
    if (!blob?.size) return false;
  }
  return true;
}

async function deleteLegacyMediaDb(): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(LEGACY_DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('legacy media delete failed'));
    req.onblocked = () => resolve();
  });
}

export async function migrateLegacyMediaDb(targetDb: IDBDatabase): Promise<number> {
  if (typeof indexedDB === 'undefined') return 0;
  const legacy = await openLegacyMediaDb();
  if (!legacy) return 0;
  try {
    const pairs = await readLegacyBlobPairs(legacy);
    if (pairs.length === 0) return 0;
    const keys = await writeMediaBlobPairs(targetDb, pairs);
    if (!(await verifyMigratedKeys(targetDb, keys))) {
      throw new Error('legacy media migration verify failed');
    }
    await deleteLegacyMediaDb();
    // oxlint-disable-next-line no-console -- one-time legacy migration visibility
    console.info(`[local-media] migrated ${pairs.length} blob(s) from ${LEGACY_DB_NAME}`);
    return pairs.length;
  } finally {
    legacy.close();
  }
}
